import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquare, Mail, FileCheck2, Bell, Check, UserPlus, Link2, AlertTriangle, DollarSign, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

function timeWaiting(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso); const now = new Date();
    const mins = Math.round((now - d) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m waiting`;
    const h = Math.round(mins / 60);
    if (h < 24) return `${h}h waiting`;
    return `${Math.round(h / 24)}d waiting`;
  } catch { return ""; }
}

/**
 * UnansweredCommsCard — mirrors the three event types that fire owner
 * alerts (inbound SMS, inbound email, estimate accepted). An item clears
 * either when the business replies (auto-detected server-side via a
 * later outbound in the same channel) or when the operator clicks the
 * check mark to mark it handled. Accepted estimates clear when they are
 * converted to a job/invoice or marked handled.
 */
export default function UnansweredCommsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [createFor, setCreateFor] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [createForm, setCreateForm] = useState({ name: "" });
  const [linkForm, setLinkForm] = useState({ customer_id: "" });
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/comms/unanswered");
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const handle = async (it) => {
    setBusy(it.id);
    try {
      await api.post("/comms/unanswered/handle", { type: it.type, id: it.id });
      toast.success("Marked handled");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const openItem = (it) => {
    if (it.type === "estimate_accepted") {
      nav(`/app/estimates/${it.id}`);
    } else if (it.customer_id) {
      nav(`/app/customers/${it.customer_id}`);
    } else if (it.lead_id) {
      nav(`/app/leads/${it.lead_id}`);
    }
  };

  const iconFor = (t) => t === "estimate_accepted" ? FileCheck2 : (t === "email" ? Mail : (t === "payment_reported" ? DollarSign : (t === "gbp_review" ? Star : MessageSquare)));
  const bgFor   = (t) => t === "estimate_accepted" ? "bg-emerald-50" : "bg-orange-50";
  const fgFor   = (t) => t === "estimate_accepted" ? "text-emerald-600" : "text-[#FF5A00]";
  const labelFor = (t) => t === "estimate_accepted" ? "Estimate accepted" : (t === "email" ? "Email" : (t === "payment_reported" ? "Payment" : (t === "gbp_review" ? "Review" : "Text")));

  const doCreateLead = async () => {
    if (!createFor) return;
    setBusy(createFor.id);
    try {
      const { data } = await api.post(`/comms/unanswered/sms/${createFor.id}/create-lead`, {
        name: createForm.name || null,
      });
      toast.success("Lead created");
      setCreateFor(null);
      await load();
      if (data?.customer_id) nav(`/app/customers/${data.customer_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create lead");
    } finally { setBusy(null); }
  };

  const doLinkCustomer = async () => {
    if (!linkFor) return;
    if (!linkForm.customer_id.trim()) { toast.error("Enter a customer_id"); return; }
    setBusy(linkFor.id);
    try {
      await api.post(`/comms/unanswered/sms/${linkFor.id}/link-customer`, {
        customer_id: linkForm.customer_id.trim(),
      });
      toast.success("Linked to customer");
      setLinkFor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link");
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="unanswered-comms-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
          <Bell className="w-4 h-4 text-[#FF5A00]" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-mono-accent uppercase tracking-wider text-zinc-500">Needs your attention</div>
          <div className="font-heading text-lg font-bold" data-testid="unanswered-comms-title">
            Unanswered communications
            {items.length > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#FF5A00] text-white text-xs font-mono-accent"
                data-testid="unanswered-comms-count-pill"
              >{items.length}</span>
            )}
          </div>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4 text-center bg-zinc-50 rounded-lg" data-testid="unanswered-comms-empty">
          Nothing waiting — you're caught up.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="unanswered-comms-list">
          {items.map((it) => {
            const Icon = iconFor(it.type);
            const isUnmatchedSms = it.type === "sms" && (it.is_unmatched || !it.customer_id);
            return (
              <li key={`${it.type}-${it.id}`} className="flex items-start gap-3 py-3" data-testid={`unanswered-item-${it.id}`}>
                <button type="button" onClick={() => openItem(it)}
                   className="flex-1 flex items-start gap-3 text-left hover:bg-zinc-50 px-2 -mx-2 rounded-md transition-colors">
                  <div className={`mt-0.5 w-7 h-7 rounded-md ${bgFor(it.type)} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-3.5 h-3.5 ${fgFor(it.type)}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-sm text-zinc-900 truncate">
                        {it.customer_name || it.from || "Unknown"}
                      </span>
                      <span className="text-[9px] font-mono-accent uppercase tracking-widest text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
                        {labelFor(it.type)}
                      </span>
                      {isUnmatchedSms && (
                        <span
                          className="text-[9px] font-mono-accent uppercase tracking-widest text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                          data-testid={`unanswered-item-${it.id}-unmatched-badge`}
                        >
                          <AlertTriangle className="w-2.5 h-2.5"/> Unknown number
                        </span>
                      )}
                      <span className="text-[10px] font-mono-accent uppercase tracking-wider text-zinc-400 ml-auto">
                        {timeWaiting(it.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-zinc-600 truncate">{it.preview || "(no preview)"}</div>
                    {isUnmatchedSms && it.from && (
                      <div className="text-xs text-zinc-500 mt-0.5">From: <span className="font-mono-accent">{it.from}</span></div>
                    )}
                  </div>
                </button>
                {isUnmatchedSms ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      disabled={busy === it.id}
                      onClick={() => { setCreateForm({ name: "" }); setCreateFor(it); }}
                      data-testid={`unanswered-create-lead-${it.id}`}
                      title="Create a lead for this number"
                    >
                      <UserPlus className="w-3.5 h-3.5 mr-1"/> Lead
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      disabled={busy === it.id}
                      onClick={() => { setLinkForm({ customer_id: "" }); setLinkFor(it); }}
                      data-testid={`unanswered-link-customer-${it.id}`}
                      title="Link to an existing customer"
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1"/> Link
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      disabled={busy === it.id}
                      onClick={() => handle(it)}
                      data-testid={`unanswered-handle-${it.id}`}
                      title="Mark as handled"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm" variant="outline"
                    disabled={busy === it.id}
                    onClick={() => handle(it)}
                    data-testid={`unanswered-handle-${it.id}`}
                    title="Mark as handled"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Create-lead dialog (for unmatched SMS) */}
      <Dialog open={!!createFor} onOpenChange={(o) => { if (!o) setCreateFor(null); }}>
        <DialogContent data-testid="unanswered-create-lead-dialog">
          <DialogHeader>
            <DialogTitle>Create a lead for this number</DialogTitle>
            <DialogDescription>
              A new customer + lead will be created for this phone number so
              future texts auto-attach to the same profile.
            </DialogDescription>
          </DialogHeader>
          {createFor && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                <div><span className="font-mono-accent uppercase tracking-widest text-[10px]">From</span>: {createFor.from}</div>
                <div className="mt-1 italic text-zinc-600">"{(createFor.preview || "").slice(0, 180)}"</div>
              </div>
              <div>
                <label className="text-sm">Lead name <span className="text-zinc-400 text-xs">(optional)</span></label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ name: e.target.value })}
                  placeholder={`SMS lead · ${createFor.from || "unknown"}`}
                  autoFocus
                  data-testid="unanswered-create-lead-name"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFor(null)} disabled={busy===createFor?.id}>Cancel</Button>
            <Button
              className="bg-[#FF5A00] hover:bg-[#e65100] text-white"
              onClick={doCreateLead}
              disabled={busy===createFor?.id}
              data-testid="unanswered-create-lead-confirm"
            >
              <UserPlus className="w-4 h-4 mr-1"/> Create lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link-to-customer dialog */}
      <Dialog open={!!linkFor} onOpenChange={(o) => { if (!o) setLinkFor(null); }}>
        <DialogContent data-testid="unanswered-link-dialog">
          <DialogHeader>
            <DialogTitle>Link to an existing customer</DialogTitle>
            <DialogDescription>
              Attach this text to a customer you already have. Paste the
              customer ID from their profile URL.
            </DialogDescription>
          </DialogHeader>
          {linkFor && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                <div><span className="font-mono-accent uppercase tracking-widest text-[10px]">From</span>: {linkFor.from}</div>
                <div className="mt-1 italic text-zinc-600">"{(linkFor.preview || "").slice(0, 180)}"</div>
              </div>
              <div>
                <label className="text-sm">Customer ID</label>
                <Input
                  value={linkForm.customer_id}
                  onChange={(e) => setLinkForm({ customer_id: e.target.value })}
                  placeholder="e.g. 6a5e503a..."
                  autoFocus
                  data-testid="unanswered-link-customer-id"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkFor(null)} disabled={busy===linkFor?.id}>Cancel</Button>
            <Button
              onClick={doLinkCustomer}
              disabled={busy===linkFor?.id || !linkForm.customer_id.trim()}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="unanswered-link-confirm"
            >
              <Link2 className="w-4 h-4 mr-1"/> Link to customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
